/**
 * briefPanelSections.js
 *
 * Shared reading rules for the read-only brief panels (layout sidebar, shared
 * proof page, Creative Studio drawer). Those three surfaces used to each carry
 * their own copy of "which sections do we show, in what order, under what
 * label" — this module is the single source so they cannot drift apart.
 *
 * Two section dialects reach these helpers:
 *   - snapshot  (`options.linked_brief`, BriefLayoutLinkService): `type_id`,
 *     plus `label` / `is_image` resolved server-side.
 *   - editor    (brief API / BriefDetailPage): `typeId`, with the image-type
 *     name living in the brief's `payload.section_meta[typeId]`.
 * Every accessor below reads both.
 */

import { htmlIsEmpty } from '@frontend/utils/sanitizeHtml';
import { getSectionMeta } from './briefSectionUtils';

const IMAGE_SECTION_PREFIX = 'img__';

// Rendered as their own "References" block, never as a prose section.
const REFERENCE_SECTION_TYPE_IDS = ['references', 'references_inspiration'];

/** Section id, whichever dialect the section arrived in. */
export function briefSectionTypeId(section) {
  if (!section) {
    return '';
  }
  return String(section.type_id || section.typeId || '');
}

/**
 * An `img__<key>__<timestamp>` id carries no readable name on its own. Strip the
 * prefix and the disambiguating timestamp so an old snapshot (written before the
 * server started storing `label`) still reads as "Hero" rather than
 * "Img Hero 1750000000000".
 */
function humanizeTypeId(typeId) {
  let value = String(typeId || 'section');

  if (value.startsWith(IMAGE_SECTION_PREFIX)) {
    value = value.slice(IMAGE_SECTION_PREFIX.length).replace(/__\d+$/, '');
  }

  return value
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase()) || 'Section';
}

/**
 * Display label for a section heading.
 *
 * Prefers the real image-type name (snapshot `label`, or `section_meta`) and
 * falls back to a humanized type id.
 */
export function briefSectionLabel(section, payload = null) {
  if (!section) {
    return 'Section';
  }

  const meta = getSectionMeta(briefSectionTypeId(section), payload);
  const named = (meta && meta.imageTypeName) || section.label;
  if (named && String(named).trim()) {
    return String(named).trim();
  }

  return humanizeTypeId(briefSectionTypeId(section));
}

/** Is this one of the brief's requested images (rather than prose)? */
export function isBriefImageSection(section, payload = null) {
  if (!section) {
    return false;
  }
  if (section.is_image === true) {
    return true;
  }

  const meta = getSectionMeta(briefSectionTypeId(section), payload);
  if (meta && meta.kind === 'image_type') {
    return true;
  }

  return briefSectionTypeId(section).startsWith(IMAGE_SECTION_PREFIX);
}

/**
 * Sections worth rendering: visible, and not the references block (which every
 * panel draws as an image grid instead).
 *
 * Empty prose sections are dropped — an unfilled "Tone of voice" is noise. An
 * empty IMAGE section is kept, because the brief still asked for that image;
 * its name is the content.
 *
 * @param {object|null} brief
 * @param {{ limit?: number }} [options] limit caps the total, as the proof page does.
 * @returns {Array} sections in `sort_order`
 */
export function visibleBriefSections(brief, { limit = 0 } = {}) {
  const sections = Array.isArray(brief?.sections) ? brief.sections : [];
  const payload = brief && brief.payload ? brief.payload : null;

  const visible = sections
    .filter((section) => {
      if (!section || section.visible === false) return false;
      const typeId = briefSectionTypeId(section).toLowerCase();
      if (REFERENCE_SECTION_TYPE_IDS.includes(typeId)) return false;
      if (isBriefImageSection(section, payload)) return true;
      return !htmlIsEmpty(section.content);
    })
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return limit > 0 ? visible.slice(0, limit) : visible;
}

/**
 * Split the renderable sections into the prose blocks and the requested images,
 * so a panel can head the image ones with a single "Images requested" label.
 *
 * The cap (when given) applies to the combined list before splitting, keeping
 * the proof page's existing "at most 8 sections" behaviour intact.
 *
 * @returns {{ prose: Array, images: Array }}
 */
export function partitionBriefSections(brief, { limit = 0 } = {}) {
  const payload = brief && brief.payload ? brief.payload : null;
  const sections = visibleBriefSections(brief, { limit });

  return {
    prose: sections.filter((section) => !isBriefImageSection(section, payload)),
    images: sections.filter((section) => isBriefImageSection(section, payload)),
  };
}

/** Count of requested-image sections — the drawer subtitle ("3 image sections"). */
export function briefImageSectionCount(brief) {
  return partitionBriefSections(brief).images.length;
}
